/*
 * Central, config-driven page security.
 *
 * `pageSecurity` arrives on the page config from the getViewTemplates API
 * (see src/store/sagas/currentPageSagas.js) already resolved to booleans:
 *
 *   currentPage.pageConfig.pageSecurity = { Read: bool, Update: bool, Full: bool }
 *
 * Every Add / Edit / Delete affordance in the portal asks this module instead
 * of reaching into the config itself, so the rules live in exactly one place.
 *
 * Rules (identical to the hand-rolled checks this replaced):
 *   read    -> Read || Update || Full
 *   create  -> Update || Full
 *   update  -> Update || Full
 *   delete  -> Full            (destructive actions need full rights)
 *   full    -> Full
 *
 * A page whose config carries no pageSecurity block is treated as "no rights"
 * and the action is hidden, which is how the original checks behaved.
 */

export const pageSecurityActions = {
  READ: "read",
  CREATE: "create",
  UPDATE: "update",
  DELETE: "delete",
  FULL: "full",
};

/* The API sends booleans, but an unresolved config sends { groupids, users }
   objects for the same keys. Only an explicit true grants rights. */
const isGranted = (value) => value === true || value === "true";

/* Normalises any { Read, Update, Full } block. The same shape reaches us as a
   page's pageConfig.pageSecurity and as a layout's templateSecurityUI, so both
   share one set of rules. */
export const normalizeSecurity = (security) => {
  if (!security) {
    return { read: false, update: false, full: false, configured: false };
  }

  return {
    read: isGranted(security.Read),
    update: isGranted(security.Update),
    full: isGranted(security.Full),
    configured: true,
  };
};

export const getPageSecurity = (currentPage) =>
  normalizeSecurity(
    currentPage && currentPage.pageConfig && currentPage.pageConfig.pageSecurity
  );

export const hasAccess = (security, action) => {
  const { read, update, full } = normalizeSecurity(security);

  switch (action) {
    case pageSecurityActions.FULL:
    case pageSecurityActions.DELETE:
      return full;
    case pageSecurityActions.CREATE:
    case pageSecurityActions.UPDATE:
      return full || update;
    case pageSecurityActions.READ:
      return full || update || read;
    default:
      return false;
  }
};

export const hasPageAccess = (currentPage, action) =>
  hasAccess(
    currentPage && currentPage.pageConfig && currentPage.pageConfig.pageSecurity,
    action
  );

export const canRead = (currentPage) =>
  hasPageAccess(currentPage, pageSecurityActions.READ);

export const canCreate = (currentPage) =>
  hasPageAccess(currentPage, pageSecurityActions.CREATE);

export const canUpdate = (currentPage) =>
  hasPageAccess(currentPage, pageSecurityActions.UPDATE);

export const canDelete = (currentPage) =>
  hasPageAccess(currentPage, pageSecurityActions.DELETE);

export const hasFullAccess = (currentPage) =>
  hasPageAccess(currentPage, pageSecurityActions.FULL);

export const isReadOnly = (currentPage) => !canUpdate(currentPage);

/* Every permission resolved from one security block, for components that need
   more than a single answer. */
export const getAccess = (security) => ({
  canRead: hasAccess(security, pageSecurityActions.READ),
  canCreate: hasAccess(security, pageSecurityActions.CREATE),
  canUpdate: hasAccess(security, pageSecurityActions.UPDATE),
  canDelete: hasAccess(security, pageSecurityActions.DELETE),
  hasFullAccess: hasAccess(security, pageSecurityActions.FULL),
  isReadOnly: !hasAccess(security, pageSecurityActions.UPDATE),
  isConfigured: normalizeSecurity(security).configured,
});
